// src/pages/VehicleReview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { createVehicleReview, getVehicleReviews } from "../api/reviewApi";

/* ───────── Brand ───────── */
const BRAND = {
  ink: "#0B0E13",
  slate600: "#475569",
  slate200: "#E5E7EB",
  slate100: "#F1F5F9",
  white: "#FFFFFF",
  pink: "#FF2D83",
};

/* ---------- Star bar ---------- */
function StarBar({ value, onChange, size = 26 }) {
  const [hoverValue, setHoverValue] = useState(null);
  const effective = hoverValue != null ? hoverValue : value;
  const stars = [1, 2, 3, 4, 5];

  return (
    <div className="flex items-center gap-1.5">
      {stars.map((s) => {
        const active = s <= effective;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            onMouseEnter={() => setHoverValue(s)}
            onMouseLeave={() => setHoverValue(null)}
            className="p-0.5"
            aria-label={`${s} star${s > 1 ? "s" : ""}`}
          >
            <span
              className="inline-block"
              style={{
                fontSize: size,
                lineHeight: 1,
                color: BRAND.pink,
              }}
            >
              {active ? "★" : "☆"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ───────── URL helper ───────── */
function useQueryParams() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

/* ───────── Page ───────── */
export default function VehicleReview() {
  const navigate = useNavigate();
  const query = useQueryParams();
  const bookingId = query.get("bookingId");

  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) {
      setInitialLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await getVehicleReviews(Number(bookingId));
        setExisting(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Failed to load previous reviews", e);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [bookingId]);

  const submit = async () => {
    if (!bookingId) return;
    try {
      setSubmitting(true);
      await createVehicleReview(Number(bookingId), { rating, title, comment });
      window.alert("Thanks! Your vehicle review has been submitted.");
      navigate(-1);
      } catch (e) {
    console.error("Can't submit vehicle review", e);

    let message = "Can't submit right now. Please try again later.";

    if (e?.response) {
      const data = e.response.data;
      if (typeof data === "string") {
        message = data;
      } else if (data?.detail && typeof data.detail === "string") {
        message = data.detail;
      } else if (Array.isArray(data) && data[0]?.msg) {
        message = data[0].msg;
      }
    } else if (e?.message) {
      message = e.message;
    }

    alert(message);
  } finally {
    setSubmitting(false);
  }

  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto max-w-xl px-4 sm:px-6 xl:px-10 py-8 md:py-10">
        <div className="mb-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <span className="text-lg" aria-hidden>
              ←
            </span>
            <span>Back</span>
          </button>
        </div>

        <header className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
            Review Vehicle
          </h1>
          {bookingId && (
            <p className="mt-1 text-xs text-slate-500">Booking #{bookingId}</p>
          )}
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm space-y-4">
          {initialLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
              <span>Loading existing reviews…</span>
            </div>
          ) : null}

          <div className="space-y-3">
            <div>
              <p className="text-sm text-slate-700 mb-1">
                How was the vehicle?
              </p>
              <StarBar value={rating} onChange={setRating} />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Title (optional)
              </label>
              <input
                type="text"
                placeholder="Summarise your experience in a few words"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900/40"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Share your experience
              </label>
              <textarea
                placeholder="Comfort, cleanliness, AC, music, luggage space, etc."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900/40"
                style={{ resize: "vertical", minHeight: 120 }}
              />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={submitting || !bookingId}
              className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit review"}
            </button>
          </div>

          {!!existing.length && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Previous reviews
              </p>
              <div className="space-y-2.5">
                {existing.map((r, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900">
                        {r.title || "(No title)"}
                      </div>
                      {typeof r.rating === "number" && r.rating > 0 && (
                        <div className="flex items-center gap-1 text-xs text-slate-700">
                          <span className="text-[14px]" style={{ color: BRAND.pink }}>
                            ★
                          </span>
                          <span>{r.rating.toFixed(1).replace(/\.0$/, "")}</span>
                        </div>
                      )}
                    </div>
                    {r.comment && (
                      <p className="mt-1 text-[13px] leading-snug text-slate-700">
                        {r.comment}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
